/**
 * Which team's setup window is open — kept outside React state.
 *
 * A refresh that lands reloads the simulation form, and `reset()` gives every
 * team a new field-array key: the team's section remounts, and any `useState`
 * inside it starts over. A window held there would close itself at the very
 * moment it has news to show. Held here, it survives the remount.
 *
 * One slot, not one per team: the window is modal, so only one is ever open.
 */

import { useCallback, useSyncExternalStore } from 'react';

import type { ApplyPart } from '../types';

type OpenWindow = { teamId: string; part: ApplyPart } | null;

let openWindow: OpenWindow = null;
const listeners = new Set<() => void>();

function setOpenWindow(next: OpenWindow): void {
  openWindow = next;
  listeners.forEach((notify) => notify());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Close whichever window is open. */
export function closeSetupWindow(): void {
  setOpenWindow(null);
}

/** Whether this team's `part` window is open, and a setter for it. */
export function useSetupWindow(teamId: string, part: ApplyPart): [boolean, (open: boolean) => void] {
  const isOpen = useSyncExternalStore(subscribe, () => openWindow?.teamId === teamId && openWindow.part === part);
  const setOpen = useCallback((open: boolean) => setOpenWindow(open ? { teamId, part } : null), [teamId, part]);
  return [isOpen, setOpen];
}
